import {
  Control,
  Controller,
  FieldErrors,
  UseFormSetValue,
  UseFormTrigger,
  UseFormWatch,
} from 'react-hook-form';
import { useRef, useState } from 'react';
import UsersAutocomplete from '../../../shared/users-autocomplete';
import GroupsAutocomplete from '../../../shared/groups-autocomplete';
import OrganizationsAutoComplete from '../../../shared/organizations-autocomplete';
import DomainsAutoComplete from '../../../shared/domain-autocomplete';
import { PolicyAction, PolicyFormValues } from '../policy-dialog';
import z from 'zod';
import Box from '@mui/material/Box';
import FormControl from '@mui/material/FormControl';
import FormControlLabel from '@mui/material/FormControlLabel';
import FormLabel from '@mui/material/FormLabel';
import IconButton from '@mui/material/IconButton';
import Paper from '@mui/material/Paper';
import Radio from '@mui/material/Radio';
import RadioGroup from '@mui/material/RadioGroup';
import Stack from '@mui/material/Stack';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import TextField from '@mui/material/TextField';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import Delete from '@mui/icons-material/Delete';
interface AddEmailDelegateActionProps {
  setValue: UseFormSetValue<PolicyFormValues>;
  control: Control<PolicyFormValues>;
  watch: UseFormWatch<PolicyFormValues>;
  errors: FieldErrors<PolicyFormValues>;
  trigger: UseFormTrigger<PolicyFormValues>;
  domains: string[];
}

export const AddEmailDelegateActionSchema = z.object({
  type: z.literal('Add_Delegate_To_Account'),
  valueType: z.string(),
  addDelegateToAccount: z.array(z.string()).min(1, 'Delegate is required.'),
  daysUntilExpiration: z
    .number()
    .min(1, 'Days until expiration must be at least 1.')
    .max(365, 'Days until expiration cannot exceed 365.')
    .optional(),
});

export type AddDelegateActionType = z.infer<
  typeof AddEmailDelegateActionSchema
>;

// Type guard to check if an action is AddDelegateActionType
function isAddDelegateAction(
  action: PolicyAction
): action is AddDelegateActionType {
  return action.type === 'Add_Delegate_To_Account';
}

export default function AddEmailDelegateAction({
  setValue,
  watch,
  errors,
  domains,
  control,
  trigger,
}: AddEmailDelegateActionProps) {
  const actionData = watch('actionData');
  const [autoCompleteResponse, setAutoCompleteResponse] = useState('');
  const [alreadySelectedError, setAlreadySelectedError] = useState(false);
  const autocompleteKey = useRef(new Date().getTime().toString());
  const idx = actionData
    .map((action) => action.type)
    .indexOf('Add_Delegate_To_Account');

  const actionAtIndex = actionData[idx];
  if (!isAddDelegateAction(actionAtIndex)) {
    return null;
  }

  const currentAction: AddDelegateActionType = actionAtIndex;
  const error: FieldErrors<AddDelegateActionType> | undefined =
    errors?.actionData?.[idx];
  const addDelegateToAccount =
    actionData[idx] &&
    Array.isArray(currentAction.addDelegateToAccount) &&
    currentAction.addDelegateToAccount.length > 0;

  function addSelection(delegate: AddDelegateActionType) {
    setValue(
      `actionData.${idx}.addDelegateToAccount`,
      delegate.addDelegateToAccount
    );
    autocompleteKey.current = new Date().getTime().toString();
    setAutoCompleteResponse('');
  }

  function removeSelection(email: string) {
    const newList = currentAction.addDelegateToAccount!.filter(
      (userEmail) => userEmail !== email
    );
    setValue(`actionData.${idx}.addDelegateToAccount`, [...newList]);
  }

  return (
    <Stack sx={{ p: 1 }}>
      <Stack gap={1}>
        <Stack flexDirection={'row'} gap={4} alignItems={'center'}>
          <FormControl>
            <FormLabel
              sx={{
                fontWeight: 'bold',
                color: (theme) => theme.palette.text.primary,
              }}
            >
              {addDelegateToAccount
                ? 'Delegate Type (Only one target type is allowed)'
                : 'Delegate Type'}
            </FormLabel>
            <RadioGroup
              aria-labelledby="radio-buttons-group-label"
              defaultValue="User"
              name="radio-buttons-group"
              value={currentAction.valueType}
              onChange={(event) =>
                setValue(`actionData.${idx}.valueType`, event.target.value)
              }
              row
            >
              <FormControlLabel
                value="User"
                control={<Radio size="small" />}
                label="User"
                disabled={addDelegateToAccount}
              />
              <FormControlLabel
                value="Group"
                control={<Radio size="small" />}
                label="Group"
                disabled={addDelegateToAccount}
              />
              <FormControlLabel
                value="OrgUnit"
                control={<Radio size="small" />}
                label="Org Unit"
                disabled={addDelegateToAccount}
              />
            </RadioGroup>
          </FormControl>
        </Stack>
        <Stack flexDirection={'row'} gap={1}>
          {currentAction.valueType === 'User' ? (
            <Box sx={{ width: 400 }}>
              <Controller
                name={`actionData.${idx}.addDelegateToAccount`}
                control={control}
                render={({ field }) => (
                  <UsersAutocomplete
                    newKey={`${autocompleteKey.current}`}
                    autocompleteValue={autoCompleteResponse}
                    setAutoCompleteValue={(value) => {
                      if (!value) {
                        setAlreadySelectedError(false);
                        return;
                      }

                      if (currentAction.addDelegateToAccount.includes(value)) {
                        setAlreadySelectedError(true);
                        return;
                      }

                      setAlreadySelectedError(false);

                      const updated = [
                        ...currentAction.addDelegateToAccount,
                        value,
                      ];
                      field.onChange(updated);

                      addSelection({
                        addDelegateToAccount: updated,
                        valueType: currentAction.valueType,
                        type: 'Add_Delegate_To_Account',
                        daysUntilExpiration: currentAction.daysUntilExpiration,
                      });

                      trigger(`actionData.${idx}.addDelegateToAccount`);
                    }}
                    errors={
                      !!error?.addDelegateToAccount || alreadySelectedError
                    }
                    helperText={
                      alreadySelectedError
                        ? 'Delegate is already selected.'
                        : error?.addDelegateToAccount
                        ? error?.addDelegateToAccount?.message
                        : ' '
                    }
                  />
                )}
              />
            </Box>
          ) : currentAction.valueType === 'Group' ? (
            <Box sx={{ width: 400 }}>
              <Controller
                name={`actionData.${idx}.addDelegateToAccount`}
                control={control}
                render={({ field }) => (
                  <GroupsAutocomplete
                    newKey={`${autocompleteKey.current}`}
                    autocompleteValue={autoCompleteResponse}
                    setAutoCompleteValue={(value) => {
                      if (!value) {
                        setAlreadySelectedError(false);
                        return;
                      }

                      if (currentAction.addDelegateToAccount.includes(value)) {
                        setAlreadySelectedError(true);
                        return;
                      }

                      setAlreadySelectedError(false);

                      const updated = [
                        ...currentAction.addDelegateToAccount,
                        value,
                      ];
                      field.onChange(updated);

                      addSelection({
                        addDelegateToAccount: updated,
                        valueType: currentAction.valueType,
                        type: 'Add_Delegate_To_Account',
                        daysUntilExpiration: currentAction.daysUntilExpiration,
                      });

                      trigger(`actionData.${idx}.addDelegateToAccount`);
                    }}
                    errors={
                      !!error?.addDelegateToAccount || alreadySelectedError
                    }
                    helperText={
                      alreadySelectedError
                        ? 'Delegate is already selected.'
                        : error?.addDelegateToAccount
                        ? error?.addDelegateToAccount?.message
                        : ' '
                    }
                  />
                )}
              />
            </Box>
          ) : currentAction.valueType === 'OrgUnit' ? (
            <Box sx={{ width: 400 }}>
              <Controller
                name={`actionData.${idx}.addDelegateToAccount`}
                control={control}
                render={({ field }) => (
                  <OrganizationsAutoComplete
                    newKey={`${autocompleteKey.current}`}
                    size="medium"
                    autoCompleteValue={autoCompleteResponse}
                    setAutoCompleteValue={(value) => {
                      if (!value) {
                        setAlreadySelectedError(false);
                        return;
                      }

                      if (currentAction.addDelegateToAccount.includes(value)) {
                        setAlreadySelectedError(true);
                        return;
                      }

                      setAlreadySelectedError(false);

                      const updated = [
                        ...currentAction.addDelegateToAccount,
                        value,
                      ];
                      field.onChange(updated);

                      addSelection({
                        addDelegateToAccount: updated,
                        valueType: currentAction.valueType,
                        type: 'Add_Delegate_To_Account',
                        daysUntilExpiration: currentAction.daysUntilExpiration,
                      });

                      trigger(`actionData.${idx}.addDelegateToAccount`);
                    }}
                    hasError={
                      !!error?.addDelegateToAccount || alreadySelectedError
                    }
                    helperText={
                      alreadySelectedError
                        ? 'Delegate is already selected.'
                        : error?.addDelegateToAccount
                        ? error?.addDelegateToAccount?.message
                        : ' '
                    }
                  />
                )}
              />
            </Box>
          ) : (
            <Box sx={{ width: 400 }}>
              <Controller
                name={`actionData.${idx}.addDelegateToAccount`}
                control={control}
                render={({ field }) => (
                  <DomainsAutoComplete
                    newKey={`${autocompleteKey.current}`}
                    size="medium"
                    domains={domains}
                    setAutoCompleteValue={(value) => {
                      if (!value) {
                        setAlreadySelectedError(false);
                        return;
                      }

                      if (currentAction.addDelegateToAccount.includes(value)) {
                        setAlreadySelectedError(true);
                        return;
                      }

                      setAlreadySelectedError(false);

                      const updated = [
                        ...currentAction.addDelegateToAccount,
                        value,
                      ];
                      field.onChange(updated);

                      addSelection({
                        addDelegateToAccount: updated,
                        valueType: currentAction.valueType,
                        type: 'Add_Delegate_To_Account',
                        daysUntilExpiration: currentAction.daysUntilExpiration,
                      });

                      trigger(`actionData.${idx}.addDelegateToAccount`);
                    }}
                    hasError={
                      !!error?.addDelegateToAccount || alreadySelectedError
                    }
                    helperText={
                      alreadySelectedError
                        ? 'Delegate is already selected.'
                        : error?.addDelegateToAccount
                        ? error?.addDelegateToAccount?.message
                        : ' '
                    }
                  />
                )}
              />
            </Box>
          )}
        </Stack>
      </Stack>
      <Controller
        name={`actionData.${idx}.daysUntilExpiration`}
        control={control}
        render={({ field }) => (
          <TextField
            {...field}
            value={field.value ?? ''}
            onChange={(event) => {
              const value =
                event.target.value === ''
                  ? undefined
                  : parseInt(event.target.value);
              field.onChange(value);
            }}
            label="Days Until Expiration"
            error={!!error?.daysUntilExpiration}
            helperText={
              error?.daysUntilExpiration
                ? error?.daysUntilExpiration?.message
                : ' '
            }
            FormHelperTextProps={{
              sx: { textWrap: 'nowrap' },
            }}
            type="number"
            inputProps={{ min: 1, max: 365 }}
            sx={{ width: 200, my: 1 }}
          />
        )}
      />
      <Paper sx={{ minWidth: 470, maxHeight: 200 }}>
        <TableContainer sx={{ minWidth: 470, maxHeight: 200 }}>
          <Table size={'small'} stickyHeader aria-label="searched-delegates">
            <TableHead>
              <TableRow>
                <TableCell>Selected Delegates</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {currentAction.addDelegateToAccount
                ? currentAction.addDelegateToAccount.map((user) => (
                    <TableRow key={user}>
                      <TableCell>
                        <Stack
                          flexDirection="row"
                          alignItems="center"
                          justifyContent="space-between"
                        >
                          <Typography variant="body2">{user}</Typography>
                          <Tooltip arrow title="Delete">
                            <span>
                              <IconButton
                                onClick={() => {
                                  removeSelection(user);
                                }}
                                size="small"
                              >
                                <Delete />
                              </IconButton>
                            </span>
                          </Tooltip>
                        </Stack>
                      </TableCell>
                    </TableRow>
                  ))
                : null}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>
    </Stack>
  );
}
